import { Endpoint } from './types'

export const adminEndpoints: Endpoint[] = [
    {
        id: 'get-token',
        method: 'POST',
        path: '/api/admin/get-token',
        title: {
            pt: 'Gerar Token JWT',
            en: 'Generate JWT Token'
        },
        description: {
            pt: 'Gera um token JWT para autenticação de cliente nas operações da API.',
            en: 'Generate JWT token for client authentication in API operations.'
        },
        authentication: true,
        authType: {
            pt: 'Bearer Token ou x-api-key',
            en: 'Bearer Token or x-api-key'
        },
        requestBody: {
            description: {
                pt: 'ID do usuário para geração do token',
                en: 'User ID for token generation'
            },
            example: {
                userId: 'user-123',
                externalId: 'user-456'
            }
        },
        response: {
            description: {
                pt: 'Token JWT para operações de cliente',
                en: 'JWT token for client operations'
            },
            example: {
                success: true,
                token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
                expiresIn: 3600
            }
        },
        codeExamples: {
            curl: `curl -X POST https://api.emprex.com.br/api/admin/get-token \\
  -H "Authorization: Bearer YOUR_ADMIN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"userId": "user-123", "externalId": "user-456"}'`,
            javascript: `const response = await fetch('https://api.emprex.com.br/api/admin/get-token', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_ADMIN_TOKEN',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ userId: 'user-123', externalId: 'user-456' })
})`
        }
    },
    {
        id: 'collection',
        method: 'POST',
        path: '/api/admin/collection',
        title: {
            pt: 'Processar Cobrança (Retenção Percentual)',
            en: 'Process Collection (Percentage Retention)'
        },
        description: {
            pt: 'Processa cobrança de pagamento para um empréstimo específico através do modelo de retenção percentual do fluxo de caixa. Use este endpoint quando um percentual fixo dos valores repassados ao usuário final for automaticamente retido para pagamento do empréstimo.',
            en: 'Process payment collection for a specific loan through the cash flow percentage retention model. Use this endpoint when a fixed percentage of amounts transferred to the end user is automatically retained for loan payment.'
        },
        authentication: true,
        authType: {
            pt: 'Bearer Token ou x-api-key',
            en: 'Bearer Token or x-api-key'
        },
        requestBody: {
            description: {
                pt: 'Dados da cobrança incluindo ID do empréstimo, valor e data',
                en: 'Collection data including loan ID, amount, and payment date'
            },
            example: {
                loanId: 'loan-123',
                amount: 275,
                paymentDate: '2025-11-01',
                paymentMethod: 'pix'
            }
        },
        response: {
            description: {
                pt: 'Resultado do processamento da cobrança',
                en: 'Collection processing result'
            },
            example: {
                success: true,
                collectionId: 'col-789',
                status: 'processed',
                message: 'Cobrança processada com sucesso'
            }
        },
        codeExamples: {
            curl: `curl -X POST https://api.emprex.com.br/api/admin/collection \\
  -H "Authorization: Bearer YOUR_ADMIN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"loanId": "loan-123", "amount": 275, "paymentDate": "2025-11-01"}'`,
            javascript: `const response = await fetch('https://api.emprex.com.br/api/admin/collection', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_ADMIN_TOKEN',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ loanId: 'loan-123', amount: 275, paymentDate: '2025-11-01' })
})`
        }
    },
    {
        id: 'create-invoice-admin',
        method: 'POST',
        path: '/api/admin/events/create-invoice',
        title: {
            pt: 'Gerar Fatura (Parcelamento no Billing)',
            en: 'Generate Invoice (Billing Installment)'
        },
        description: {
            pt: 'Cria fatura de pagamento para parcela de empréstimo através do modelo de parcelamento incorporado ao billing da plataforma. Use este endpoint para adicionar as parcelas do empréstimo à cobrança recorrente da plataforma (ex: fatura semanal ou mensal), permitindo um modelo de pagamento previsível e integrado.',
            en: 'Create payment invoice for loan installment through the billing-integrated installment model. Use this endpoint to add loan installments to the platform\'s recurring billing (e.g., weekly or monthly invoice), enabling a predictable and integrated payment model.'
        },
        authentication: true,
        authType: {
            pt: 'Bearer Token ou x-api-key',
            en: 'Bearer Token or x-api-key'
        },
        requestBody: {
            description: {
                pt: 'Parâmetros para criação da fatura',
                en: 'Invoice creation parameters'
            },
            example: {
                loanId: 'loan-123',
                installment: 1,
                amount: 275,
                dueDate: '2025-11-01'
            }
        },
        response: {
            description: {
                pt: 'Detalhes da fatura criada',
                en: 'Created invoice details'
            },
            example: {
                success: true,
                invoice: {
                    id: 'inv-123',
                    url: 'https://payment.example.com/invoices/inv-123',
                    barcode: '34191.79001 01043.510047 91020.150008 1 84350000002000',
                    dueDate: '2025-11-01'
                }
            }
        },
        codeExamples: {
            curl: `curl -X POST https://api.emprex.com.br/api/admin/events/create-invoice \\
  -H "Authorization: Bearer YOUR_ADMIN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"loanId": "loan-123", "installment": 1, "amount": 275, "dueDate": "2025-11-01"}'`,
            javascript: `const response = await fetch('https://api.emprex.com.br/api/admin/events/create-invoice', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_ADMIN_TOKEN',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ loanId: 'loan-123', installment: 1, amount: 275, dueDate: '2025-11-01' })
})`
        }
    },
    {
        id: 'loan-details',
        method: 'GET',
        path: '/api/admin/loan-details',
        title: {
            pt: 'Obter Detalhes do Empréstimo',
            en: 'Get Loan Details'
        },
        description: {
            pt: 'Recupera informações detalhadas de um empréstimo (relatório administrativo).',
            en: 'Retrieve detailed loan information (administrative report).'
        },
        authentication: true,
        authType: {
            pt: 'Bearer Token ou x-api-key',
            en: 'Bearer Token or x-api-key'
        },
        parameters: [
            {
                name: 'loanId',
                type: 'string',
                required: true,
                description: {
                    pt: 'ID do empréstimo',
                    en: 'Loan ID'
                }
            }
        ],
        response: {
            description: {
                pt: 'Dados detalhados do empréstimo',
                en: 'Detailed loan data'
            },
            example: {
                success: true,
                loan: {
                    id: 'loan-123',
                    userId: 'user-123',
                    amount: 1000,
                    totalAmount: 1100,
                    installments: 4,
                    status: 'active',
                    createdAt: '2025-10-01T10:00:00Z',
                    approvedAt: '2025-10-01T11:00:00Z'
                }
            }
        },
        codeExamples: {
            curl: `curl -X GET "https://api.emprex.com.br/api/admin/loan-details?loanId=loan-123" \\
  -H "Authorization: Bearer YOUR_ADMIN_TOKEN"`,
            javascript: `const response = await fetch('https://api.emprex.com.br/api/admin/loan-details?loanId=loan-123', {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer YOUR_ADMIN_TOKEN'
  }
})`
        }
    },
    {
        id: 'payment-details',
        method: 'GET',
        path: '/api/admin/payment-details',
        title: {
            pt: 'Obter Detalhes de Pagamento',
            en: 'Get Payment Details'
        },
        description: {
            pt: 'Recupera histórico de pagamentos e detalhes (relatório administrativo).',
            en: 'Retrieve payment history and details (administrative report).'
        },
        authentication: true,
        authType: {
            pt: 'Bearer Token ou x-api-key',
            en: 'Bearer Token or x-api-key'
        },
        parameters: [
            {
                name: 'loanId',
                type: 'string',
                required: false,
                description: {
                    pt: 'ID do empréstimo (opcional)',
                    en: 'Loan ID (optional)'
                }
            },
            {
                name: 'startDate',
                type: 'string',
                required: false,
                description: {
                    pt: 'Data inicial do período (opcional)',
                    en: 'Period start date (optional)'
                }
            },
            {
                name: 'endDate',
                type: 'string',
                required: false,
                description: {
                    pt: 'Data final do período (opcional)',
                    en: 'Period end date (optional)'
                }
            }
        ],
        response: {
            description: {
                pt: 'Dados do histórico de pagamentos',
                en: 'Payment history data'
            },
            example: {
                success: true,
                payments: [
                    {
                        id: 'pay-456',
                        loanId: 'loan-123',
                        amount: 275,
                        paymentDate: '2025-10-15',
                        status: 'completed',
                        method: 'pix'
                    },
                    {
                        id: 'pay-457',
                        loanId: 'loan-123',
                        amount: 275,
                        paymentDate: '2025-11-15',
                        status: 'pending',
                        method: 'boleto'
                    }
                ]
            }
        },
        codeExamples: {
            curl: `curl -X GET "https://api.emprex.com.br/api/admin/payment-details?loanId=loan-123" \\
  -H "Authorization: Bearer YOUR_ADMIN_TOKEN"`,
            javascript: `const response = await fetch('https://api.emprex.com.br/api/admin/payment-details?loanId=loan-123', {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer YOUR_ADMIN_TOKEN'
  }
})`
        }
    }
]
